import {
    type ShiftSuggestion,
    type ShiftTab,
} from '@apps/web/components/phoebe_chat/assistant_response_types';
import { formatDateAndTime, formatTime } from '@apps/web/lib/date_utils';
import { buildOutreachDisplayUnits } from '@apps/web/lib/outreach_shift_ordering';

export type ShiftSuggestionToolOutput = {
    readonly toolName: string | null;
    readonly outputJson: unknown;
};

export type ShiftSuggestionToolCall = {
    readonly toolName: string | null;
    readonly argumentsJson: unknown;
    readonly sequence: number;
};

export type ShiftSuggestionTimelineMarker = {
    readonly sequence: number;
    readonly role: 'user' | 'assistant';
    readonly hasText: boolean;
};

export type ShiftSuggestionGroupSource = {
    readonly id?: string;
    readonly shiftGroupId?: string;
    readonly shift_group_id?: string;
    readonly shiftIds?: ReadonlyArray<string>;
    readonly shift_ids?: ReadonlyArray<string>;
};

export type ShiftSuggestionShiftSource = {
    readonly id?: string;
    readonly shift_id?: string;
    readonly patientName?: string | null;
    readonly patient_name?: string | null;
    readonly shiftStartTime?: string | null;
    readonly shift_start_time?: string | null;
    readonly shiftEndTime?: string | null;
    readonly shift_end_time?: string | null;
    readonly start_time?: string | null;
    readonly end_time?: string | null;
};

export function buildShiftSuggestionTabs({
    toolOutputs,
    toolCalls,
    timelineMarkers,
    timezone,
    outreachShiftGroups = [],
    outreachShifts = [],
}: {
    readonly toolOutputs: Array<ShiftSuggestionToolOutput>;
    readonly toolCalls: Array<ShiftSuggestionToolCall>;
    readonly timelineMarkers: Array<ShiftSuggestionTimelineMarker>;
    readonly timezone: string;
    readonly outreachShiftGroups?: ReadonlyArray<ShiftSuggestionGroupSource>;
    readonly outreachShifts?: ReadonlyArray<ShiftSuggestionShiftSource>;
}): {
    readonly tabs: Array<ShiftTab>;
    readonly turnSequence: number;
} | null {
    const shiftLabelMap = new Map<
        string,
        {
            readonly patientName: string;
            readonly startTime: string;
            readonly endTime: string;
        }
    >();
    const groupMemberShiftIds = new Map<string, Array<string>>();
    const groupIndexById = new Map<string, number>();
    const groupIdByMemberShiftId = new Map<string, string>();

    const registerShift = (shift: ShiftSuggestionShiftSource) => {
        const shiftId = shift.shift_id ?? shift.id ?? null;
        if (!shiftId) {
            return;
        }
        const startTime =
            shift.start_time ??
            shift.shift_start_time ??
            shift.shiftStartTime ??
            '';
        const endTime =
            shift.end_time ?? shift.shift_end_time ?? shift.shiftEndTime ?? '';
        shiftLabelMap.set(shiftId, {
            patientName: shift.patient_name ?? shift.patientName ?? 'Unknown',
            startTime,
            endTime,
        });
    };

    const registerGroup = (group: ShiftSuggestionGroupSource) => {
        const groupId =
            group.shift_group_id ?? group.shiftGroupId ?? group.id ?? null;
        const rawMemberIds = group.shift_ids ?? group.shiftIds ?? [];
        const memberIds = Array.isArray(rawMemberIds)
            ? rawMemberIds.filter((id): id is string => typeof id === 'string')
            : [];
        if (!groupId) {
            return;
        }
        groupMemberShiftIds.set(groupId, memberIds);
        for (const memberId of memberIds) {
            if (!groupIdByMemberShiftId.has(memberId)) {
                groupIdByMemberShiftId.set(memberId, groupId);
            }
        }
        if (!groupIndexById.has(groupId)) {
            groupIndexById.set(groupId, groupIndexById.size + 1);
        }
    };

    for (const shift of outreachShifts) {
        registerShift(shift);
    }
    for (const group of outreachShiftGroups) {
        registerGroup(group);
    }
    const tabOrderByKey = new Map<string, number>();
    buildOutreachDisplayUnits(
        outreachShifts.flatMap((shift) => {
            const shiftId = shift.id ?? shift.shift_id ?? null;
            const shiftStartTime = shift.shift_start_time ?? shift.start_time;
            if (!shiftId || !shiftStartTime) {
                return [];
            }
            return [{ id: shiftId, shift_start_time: shiftStartTime }];
        }),
        outreachShiftGroups,
    ).forEach((unit, index) => {
        tabOrderByKey.set(unit.id, index);
        for (const shiftId of unit.shiftIds) {
            tabOrderByKey.set(shiftId, index);
        }
    });

    for (const toolOutput of toolOutputs) {
        if (
            !toolOutput.outputJson ||
            typeof toolOutput.outputJson !== 'object' ||
            Array.isArray(toolOutput.outputJson)
        ) {
            continue;
        }

        const data = toolOutput.outputJson as Record<string, unknown>;
        if (toolOutput.toolName !== 'get_outreach_details') {
            continue;
        }

        const shifts = data['shifts'];
        if (Array.isArray(shifts)) {
            for (const shift of shifts) {
                if (
                    !shift ||
                    typeof shift !== 'object' ||
                    Array.isArray(shift)
                ) {
                    continue;
                }
                const s = shift as Record<string, unknown>;
                registerShift({
                    shift_id:
                        typeof s['shift_id'] === 'string'
                            ? s['shift_id']
                            : undefined,
                    patient_name:
                        typeof s['patient_name'] === 'string'
                            ? s['patient_name']
                            : undefined,
                    start_time:
                        typeof s['start_time'] === 'string'
                            ? s['start_time']
                            : undefined,
                    end_time:
                        typeof s['end_time'] === 'string'
                            ? s['end_time']
                            : typeof s['shift_end_time'] === 'string'
                              ? s['shift_end_time']
                              : undefined,
                });
            }
        }

        const shiftGroups = data['shift_groups'];
        if (Array.isArray(shiftGroups)) {
            for (const group of shiftGroups) {
                if (
                    !group ||
                    typeof group !== 'object' ||
                    Array.isArray(group)
                ) {
                    continue;
                }
                const g = group as Record<string, unknown>;
                registerGroup({
                    shift_group_id:
                        typeof g['shift_group_id'] === 'string'
                            ? g['shift_group_id']
                            : undefined,
                    shiftGroupId:
                        typeof g['shiftGroupId'] === 'string'
                            ? g['shiftGroupId']
                            : undefined,
                    id: typeof g['id'] === 'string' ? g['id'] : undefined,
                    shift_ids: Array.isArray(g['shift_ids'])
                        ? g['shift_ids'].filter(
                              (id): id is string => typeof id === 'string',
                          )
                        : [],
                    shiftIds: Array.isArray(g['shiftIds'])
                        ? g['shiftIds'].filter(
                              (id): id is string => typeof id === 'string',
                          )
                        : [],
                });
            }
        }
    }

    const suggestionsByShift = new Map<string, Array<ShiftSuggestion>>();
    let suggestionsTurnSequence: number | null = null;
    for (const toolCall of toolCalls) {
        if (
            toolCall.toolName !== 'add_outreach_suggestions' ||
            !toolCall.argumentsJson ||
            typeof toolCall.argumentsJson !== 'object' ||
            Array.isArray(toolCall.argumentsJson)
        ) {
            continue;
        }

        const args = toolCall.argumentsJson as Record<string, unknown>;
        const modeArg =
            typeof args['mode'] === 'string'
                ? args['mode'].trim().toLowerCase()
                : '';
        if (modeArg === 'overwrite') {
            suggestionsByShift.clear();
            suggestionsTurnSequence = null;
        }
        if (suggestionsTurnSequence === null) {
            suggestionsTurnSequence = toolCall.sequence;
        }
        const suggestions = args['suggestions'];
        if (!Array.isArray(suggestions)) {
            continue;
        }

        for (const s of suggestions) {
            if (!s || typeof s !== 'object' || Array.isArray(s)) {
                continue;
            }
            const suggestionRecord = s as Record<string, unknown>;
            const name =
                typeof suggestionRecord['caregiver_name'] === 'string'
                    ? suggestionRecord['caregiver_name']
                    : null;
            const id =
                typeof suggestionRecord['caregiver_id'] === 'string'
                    ? suggestionRecord['caregiver_id']
                    : null;
            const reasoning =
                typeof suggestionRecord['reasoning'] === 'string'
                    ? suggestionRecord['reasoning']
                    : '';
            const coveredShiftIds = Array.isArray(
                suggestionRecord['covered_shift_ids'],
            )
                ? suggestionRecord['covered_shift_ids']
                : [];
            const coveredShiftGroupIds = Array.isArray(
                suggestionRecord['covered_shift_group_ids'],
            )
                ? suggestionRecord['covered_shift_group_ids']
                : [];
            if (!name || !id) {
                continue;
            }

            const suggestion: ShiftSuggestion = {
                caregiverId: id,
                caregiverName: name,
                reasoning,
            };
            const bucketKeys: Array<string> = [];
            for (const shiftId of coveredShiftIds) {
                if (typeof shiftId === 'string') {
                    bucketKeys.push(
                        groupIdByMemberShiftId.get(shiftId) ?? shiftId,
                    );
                }
            }
            for (const groupId of coveredShiftGroupIds) {
                if (typeof groupId === 'string') {
                    if (!groupIndexById.has(groupId)) {
                        groupIndexById.set(groupId, groupIndexById.size + 1);
                    }
                    bucketKeys.push(groupId);
                }
            }
            for (const key of new Set(bucketKeys)) {
                const existing = suggestionsByShift.get(key);
                if (existing) {
                    if (
                        !existing.some(
                            (e) => e.caregiverId === suggestion.caregiverId,
                        )
                    ) {
                        existing.push(suggestion);
                    }
                } else {
                    suggestionsByShift.set(key, [suggestion]);
                }
            }
        }
    }

    if (suggestionsByShift.size <= 1 || suggestionsTurnSequence === null) {
        return null;
    }

    let displayTurnSequence = suggestionsTurnSequence;
    const orderedMarkers = [...timelineMarkers].sort(
        (a, b) => a.sequence - b.sequence,
    );
    for (const marker of orderedMarkers) {
        if (marker.sequence < suggestionsTurnSequence) {
            continue;
        }
        if (
            marker.sequence > suggestionsTurnSequence &&
            marker.role === 'user'
        ) {
            break;
        }
        if (marker.role === 'assistant' && marker.hasText) {
            displayTurnSequence = marker.sequence;
        }
    }

    const orderedSuggestionEntries = [...suggestionsByShift.entries()].sort(
        ([leftShiftId], [rightShiftId]) => {
            const leftOrder =
                tabOrderByKey.get(leftShiftId) ?? Number.MAX_SAFE_INTEGER;
            const rightOrder =
                tabOrderByKey.get(rightShiftId) ?? Number.MAX_SAFE_INTEGER;
            if (leftOrder !== rightOrder) {
                return leftOrder - rightOrder;
            }
            return leftShiftId.localeCompare(rightShiftId);
        },
    );

    let standaloneShiftCounter = 0;
    const tabs: Array<ShiftTab> = [];
    const buildShiftHeaderRow = (shift: {
        readonly patientName: string;
        readonly startTime: string;
        readonly endTime: string;
    }) => {
        if (!shift.startTime) {
            return shift.patientName;
        }
        const timeLabel = shift.endTime
            ? `${formatDateAndTime(shift.startTime, timezone)} - ${formatTime(shift.endTime, timezone)}`
            : formatDateAndTime(shift.startTime, timezone);
        return `${shift.patientName} - ${timeLabel}`;
    };
    for (const [shiftId, suggestions] of orderedSuggestionEntries) {
        const info = shiftLabelMap.get(shiftId);
        const groupIndex = groupIndexById.get(shiftId);
        let label: string;
        let headerRows: Array<string>;
        if (groupIndex !== undefined) {
            const memberIds = groupMemberShiftIds.get(shiftId);
            const memberInfos = memberIds
                ?.map((id) => shiftLabelMap.get(id))
                .filter(
                    (
                        entry,
                    ): entry is {
                        readonly patientName: string;
                        readonly startTime: string;
                        readonly endTime: string;
                    } => !!entry?.startTime,
                );
            if (memberInfos && memberInfos.length > 0) {
                const sorted = [...memberInfos].sort((a, b) =>
                    a.startTime.localeCompare(b.startTime),
                );
                const first = sorted[0];
                const patientNames = Array.from(
                    new Set(sorted.map((m) => m.patientName)),
                ).join(', ');
                label = first
                    ? `Group ${groupIndex}: ${formatDateAndTime(first.startTime, timezone)} - ${patientNames} (${sorted.length} shifts)`
                    : `Group ${groupIndex}`;
                headerRows = sorted.map(buildShiftHeaderRow);
            } else {
                label = `Group ${groupIndex}`;
                headerRows = [label];
            }
        } else if (info?.startTime) {
            standaloneShiftCounter += 1;
            label = `${formatDateAndTime(info.startTime, timezone)} - ${info.patientName}`;
            headerRows = [buildShiftHeaderRow(info)];
        } else {
            const memberIds = groupMemberShiftIds.get(shiftId);
            const memberInfos = memberIds
                ?.map((id) => shiftLabelMap.get(id))
                .filter(
                    (
                        entry,
                    ): entry is {
                        readonly patientName: string;
                        readonly startTime: string;
                        readonly endTime: string;
                    } => !!entry?.startTime,
                );
            if (memberInfos && memberInfos.length > 0) {
                const sorted = [...memberInfos].sort((a, b) =>
                    a.startTime.localeCompare(b.startTime),
                );
                const first = sorted[0];
                const patientNames = Array.from(
                    new Set(sorted.map((m) => m.patientName)),
                ).join(', ');
                label = first
                    ? `Group ${groupIndex}: ${formatDateAndTime(first.startTime, timezone)} - ${patientNames} (${sorted.length} shifts)`
                    : `Group ${groupIndex}`;
                headerRows = sorted.map(buildShiftHeaderRow);
            } else {
                standaloneShiftCounter += 1;
                label = `Shift ${standaloneShiftCounter}`;
                headerRows = [label];
            }
        }
        tabs.push({ shiftId, label, headerRows, suggestions });
    }

    return { tabs, turnSequence: displayTurnSequence };
}
